import { useFormik } from 'formik';
import IBAN from 'iban';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React, { useEffect, useState } from 'react';
import { IoSearch } from 'react-icons/io5';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router-dom';
import { Switch } from 'rizzui';
import * as yup from 'yup';
import { CNAEModalSelector } from '../../components/forms/CNAEModalSelector';
import CustomSelectApiHookForm from '../../components/forms/CustomSelectApiHookForms';
import RequiredInput from '../../components/forms/InputHOC';
import PhoneInput from '../../components/forms/PhoneInputWrapper';
import SelectProvince from '../../components/forms/SelectProvince';
import useConsultancies from '../../hooks/api-calls/useConsultancies';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { Client } from '../../type/entities/client-type';
import { validateCifNif, validateSS } from '../../utils/validatorFunctions';
import FormFooter from '../_layout/_footers/form-footer';

interface ClientFormProps {
    isLoading: boolean;
    submit: Function;
    data?: Client | undefined;
}

const normalizePhoneForComparison = (phone?: string | null): string => {
    if (!phone || phone.trim() === '') {
        return '';
    }

    const parsedPhone = parsePhoneNumberFromString(phone, 'ES');
    return parsedPhone?.number || phone.replace(/\D+/g, '');
};

const areDuplicateClientPhones = (first?: string | null, second?: string | null): boolean => {
    const firstNormalized = normalizePhoneForComparison(first);
    const secondNormalized = normalizePhoneForComparison(second);

    return firstNormalized !== '' && firstNormalized === secondNormalized;
};

const schema = yup.object({
    cifNif: yup.string().test('valid-cif-nif', 'El CIF/NIF no es válido', (value) => {
        if (value !== undefined && value.length > 0) {
            return validateCifNif(value);
        } else {
            return true;
        }
    }).required('Campo obligatorio'),
    companyName: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').required('Campo obligatorio'),
    registeredOfficeAddress: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    registeredOfficeTown: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    registeredOfficeProvince: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    registeredOfficePostalCode: yup.string().matches(/^[0-9]{5}$/, 'Código postal no válido').required('Campo obligatorio'),
    cnae: yup.string().matches(/^[0-9]{4}$/, 'CNAE no válido').required('Campo obligatorio'),
    activityDescription: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    iban: yup.string().test('iban', 'El formato de IBAN no es válido', (value: any) => IBAN.isValid(value)),
    email: yup.string().email('Email no válido').required('Campo obligatorio'),
    telephone: yup.string().required('El teléfono es obligatorio').test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
    telephoneSecondary: yup.string().nullable().notRequired().nullable().test(
        'unique-client-telephone-secondary',
        'El teléfono secundario no puede coincidir con el teléfono o el móvil',
        function (value) {
            const { telephone, mobile } = this.parent;
            return !areDuplicateClientPhones(value, telephone) && !areDuplicateClientPhones(value, mobile);
        }
    ),
    consultancyId: yup.string().nullable(),
    personResponsibleName: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    personResponsibleNif: yup.string().test('valid-cif-nif', 'El CIF/NIF no es válido', (value) => {
        if (value !== undefined && value.length > 0) {
            return validateCifNif(value);
        } else {
            return true;
        }
    }).required('Campo obligatorio'),
    personResponsibleEmail: yup.string().email('Email no válido').required('Campo obligatorio'),
    personResponsibleTelephone: yup.string().required('El teléfono es obligatorio').test("valid-phone", "El teléfono no es válido", (value) => isValidPhoneNumber(value)),
    personResponsibleTelephoneSecondary: yup.string().nullable().notRequired().nullable(),
    companyCreationDate: yup.string().nullable(),
    socialSecurityNumber: yup.string().test('valid-ss', 'El número de la SS no es válido', (value) => {
        if (value !== undefined && value.length > 0) {
            return validateSS(value);
        } else {
            return true;
        }
    }).nullable(),
    workersNumber: yup.number().nullable().min(0, 'Número de trabajadores no válido'),
    hasRlt: yup.mixed().required('Campo obligatorio').oneOf([true, false], 'Debe indicar si la empresa tiene RLT'),
    mobile: yup.string().nullable()
        .test("valid-phone", "El teléfono no es válido", (value) => value === undefined || isValidPhoneNumber(value ?? ''))
        .test(
            'unique-client-mobile',
            'El móvil no puede coincidir con el teléfono o el teléfono secundario',
            function (value) {
                const { telephone, telephoneSecondary } = this.parent;
                return !areDuplicateClientPhones(value, telephone) && !areDuplicateClientPhones(value, telephoneSecondary);
            }
        ),
    bic: yup.string().nullable().notRequired().test('bic', 'El formato de BIC no es válido', (value: any) => {
        if (value !== undefined && value !== null && value.length > 0) {
            return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value);
        } else {
            return true;
        }
    }),
});

const ClientForm: React.FC<ClientFormProps> = ({ isLoading, submit, data }) => {

    const mode = data ? 'Editar' : 'Crear';
    const navigate = useNavigate();
    const { fetchConsultancies } = useConsultancies({ active: true });

    const [CNAESelectorVisible, setCNAESelectorVisible] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            clientId: data?.id ?? '',
            cifNif: data?.cifNif ?? '',
            companyName: data?.companyName ?? '',
            registeredOfficeAddress: data?.registeredOfficeAddress ?? '',
            registeredOfficeTown: data?.registeredOfficeTown ?? '',
            registeredOfficeProvince: data?.registeredOfficeProvince ?? '',
            registeredOfficePostalCode: data?.registeredOfficePostalCode ?? '',
            cnae: data?.cnae ?? '',
            activityDescription: data?.activityDescription ?? '',
            iban: data?.iban ?? '',
            email: data?.email ?? '',
            telephone: data?.telephone ?? '',
            telephoneSecondary: data?.telephoneSecondary ?? '',
            consultancyId: data?.consultancy?.id ?? '',
            consultancyName: data?.consultancy?.name ?? '', // only used to display the name in the selector
            personResponsibleName: data?.personResponsibleName ?? '',
            personResponsibleNif: data?.personResponsibleNif ?? '',
            personResponsibleEmail: data?.personResponsibleEmail ?? '',
            personResponsibleTelephone: data?.personResponsibleTelephone ?? '',
            personResponsibleTelephoneSecondary: data?.personResponsibleTelephoneSecondary ?? '',
            companyCreationDate: data?.companyCreationDate?.date.split(' ')[0] ?? '',
            socialSecurityNumber: data?.socialSecurityNumber ?? '',
            workersNumber: data?.workersNumber ?? 0,
            hasRlt: data?.hasRlt ?? null,
            mobile: data?.mobile ?? '',
            bic: data?.bic ?? '',
        },
        validationSchema: schema,
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => { submit(values) },
    });

    /**
     * Validate form when the cnae and the activity are filled
     */
    useEffect(() => {
        if (formik.values.cnae && formik.values.activityDescription) {
            formik.validateForm();
        }
    }, [formik.values.cnae, formik.values.activityDescription]);

    const getContent = () => {
        return (
            <>
                <div className="@container">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 font-medium">
                            <FormGroup
                                title="Información General"
                                description="Datos principales"
                                titleCols="@md:col-span-2"
                                childCols="@md:col-span-10 md:grid-cols-4"
                            >
                                <RequiredInput
                                    id='cifNif'
                                    label="CIF/NIF"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='companyName'
                                    label="Razón Social"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='email'
                                    type="email"
                                    label="Email"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='cnae'
                                    readOnly
                                    label="CNAE"
                                    suffix={<IoSearch className="cursor-pointer" size={'20px'} onClick={() => { setCNAESelectorVisible(true) }} />}
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='activityDescription'
                                    readOnly
                                    label="Descripción de la actividad"
                                    className="md:col-span-2"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='iban'
                                    label="IBAN"
                                    formik={formik}
                                />

                                <PhoneInput
                                    id='telephone'
                                    label='Teléfono'
                                    onBlur={formik.handleBlur}
                                    onChange={e => { formik.setFieldValue('telephone', e) }}
                                    value={formik.values.telephone}
                                    error={formik.errors.telephone}
                                />

                                <PhoneInput
                                    id='telephoneSecondary'
                                    label='Teléfono Secundario'
                                    onBlur={formik.handleBlur}
                                    onChange={e => { formik.setFieldValue('telephoneSecondary', e) }}
                                    value={formik.values.telephoneSecondary}
                                    error={formik.errors.telephoneSecondary}
                                    required={false}
                                />

                                <PhoneInput
                                    id='mobile'
                                    label="Móvil"
                                    onBlur={formik.handleBlur}
                                    onChange={e => { formik.setFieldValue('mobile', e) }}
                                    value={formik.values.mobile}
                                    error={formik.errors.mobile as string}
                                    required={false}
                                />

                                <CustomSelectApiHookForm
                                    id="consultancyId"
                                    label="Asesoría"
                                    formik={formik}
                                    fetchOptions={async (searchText: string | null) => {
                                        const text = searchText || formik.values.consultancyName;
                                        return fetchConsultancies({ search_text: text }) as any;
                                    }}
                                    error={formik.errors.consultancyId}
                                    minInputLength={3}
                                    containerClassName='col-span-2'
                                />

                                <RequiredInput
                                    id='companyCreationDate'
                                    type="date"
                                    label="Fecha de creación"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='socialSecurityNumber'
                                    label="Número de Seguridad Social"
                                    className='md:col-span-2'
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='workersNumber'
                                    type="number"
                                    label="Número de trabajadores"
                                    formik={formik}
                                />

                                <div className="flex flex-col gap-2">
                                    <span className="text-sm font-medium text-gray-900">
                                        Empresa con RLT <span className="text-red-500">*</span>
                                    </span>
                                    <div className="flex items-center gap-3">
                                        <Switch
                                            id="hasRlt"
                                            label={formik.values.hasRlt === true ? 'Sí' : formik.values.hasRlt === false ? 'No' : 'Sin seleccionar'}
                                            checked={formik.values.hasRlt === true}
                                            onChange={() => {
                                                const nextValue = formik.values.hasRlt === true ? false : true;
                                                formik.setFieldValue('hasRlt', nextValue);
                                            }}
                                            switchKnobClassName="bg-secondary"
                                        />
                                    </div>
                                    {formik.errors.hasRlt && (
                                        <span className="text-xs text-red-500">{formik.errors.hasRlt as string}</span>
                                    )}
                                </div>

                                <RequiredInput
                                    id='bic'
                                    label="Código BIC"
                                    className='md:col-span-3'
                                    formik={formik}
                                    required={false}
                                />
                            </FormGroup>

                            <FormGroup
                                title="Domilicio Social"
                                description="Datos de la dirección"
                                className='pt-6'
                                titleCols="@md:col-span-2"
                                childCols="@md:col-span-10 md:grid-cols-6"
                            >
                                <SelectProvince
                                    required
                                    id='registeredOfficeProvince'
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='registeredOfficeTown'
                                    label="Población"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='registeredOfficeAddress'
                                    label="Dirección"
                                    className='md:col-span-3'
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='registeredOfficePostalCode'
                                    label="Código Postal"
                                    formik={formik}
                                />
                            </FormGroup>

                            <FormGroup
                                title="Responsable"
                                description="Datos del responsable"
                                className='pt-6'
                                titleCols="@md:col-span-2"
                                childCols="@md:col-span-10 md:grid-cols-3"
                            >
                                <RequiredInput
                                    id='personResponsibleNif'
                                    label="CIF/NIF"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='personResponsibleName'
                                    label="Nombre y apellidos"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='personResponsibleEmail'
                                    label="Email"
                                    formik={formik}
                                />

                                <PhoneInput
                                    id='personResponsibleTelephone'
                                    label='Teléfono'
                                    onBlur={formik.handleBlur}
                                    onChange={e => { formik.setFieldValue('personResponsibleTelephone', e) }}
                                    value={formik.values.personResponsibleTelephone}
                                    error={formik.errors.personResponsibleTelephone}
                                />

                                <PhoneInput
                                    id='personResponsibleTelephoneSecondary'
                                    label='Teléfono Secundario'
                                    onBlur={formik.handleBlur}
                                    onChange={e => { formik.setFieldValue('personResponsibleTelephoneSecondary', e) }}
                                    value={formik.values.personResponsibleTelephoneSecondary}
                                    error={formik.errors.personResponsibleTelephoneSecondary}
                                    required={false}
                                />
                            </FormGroup>
                        </div>
                    </form>
                </div>

                <CNAEModalSelector
                    visible={CNAESelectorVisible}
                    setVisible={setCNAESelectorVisible}
                    onSelect={(cnae: string, description: string) => {
                        formik.setFieldValue('cnae', cnae);
                        formik.setFieldValue('activityDescription', description);
                        setCNAESelectorVisible(false);
                    }}
                />
            </>
        );
    };

    return (
        <>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Cliente'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </>
    );
};

export default ClientForm;
